import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as Modal from '_components/modal/generic-modal'
import { Button, Loader } from '@refera/ui-web'
import { Typography, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch } from 'react-redux'
import { useParams } from '@reach/router'
import { useToast } from '_hooks/use-toast'
import { formatErrorMessage } from '_views/finance/utils/FormatErrorMessage'
import { getServiceOrder, openWarrantySaas } from '_modules/service-orders/actions'
import { getHistoryLogs } from '_modules/history-logs/actions'
import TextField from '_components/textfield'
import { Attachments } from '_components/refera-ui'

const MAX_DETAILS_LENGTH = 250

const useStyles = makeStyles(({ spacing }) => ({
  titleTight: {
    '&&': {
      paddingBottom: 8,
    },
  },
  dividerTight: {
    marginBottom: 8,
  },
  contentTight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing(24),
    padding: `${spacing(12, 20, 40, 20)} !important`,
  },
}))

export default function WarrantyOpeningModal({
  isOpen,
  handleClose,
  serviceOrderId: propServiceOrderId,
  title,
  introText,
  confirmAction,
  onSuccess,
  successToastMessage,
}) {
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { serviceOrderId: paramsServiceOrderId } = useParams()
  const serviceOrderId = propServiceOrderId || paramsServiceOrderId
  const [isLoading, setIsLoading] = useState(false)
  const [details, setDetails] = useState('')
  const [attachmentsImages, setAttachmentsImages] = useState([])
  const [attachmentsPdf, setAttachmentsPdf] = useState([])
  const isMountedRef = useRef(true)
  const styles = useStyles()

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      setIsLoading(true)

      const allAttachments = [...attachmentsImages, ...attachmentsPdf]
      if (confirmAction) {
        await confirmAction({ dispatch, serviceOrderId, details, attachments: allAttachments })
      } else {
        const payload = {
          origin: 'comment',
          type: 'request',
          service_order: serviceOrderId,
          subject: 'others',
          message: details,
          resolved: true,
          visibleTo: 'PUBLIC',
          message_attachment: allAttachments,
        }

        await dispatch(openWarrantySaas(serviceOrderId, payload))
      }
      if (!isMountedRef.current) return

      if (onSuccess) {
        await onSuccess({ dispatch, serviceOrderId })
      } else {
        await dispatch(getServiceOrder(serviceOrderId))
        if (!isMountedRef.current) return
        await dispatch(getHistoryLogs(serviceOrderId, true))
      }
      if (!isMountedRef.current) return
      showToast({ type: 'success', message: successToastMessage })
      if (!isMountedRef.current) return
      setDetails('')
      setAttachmentsImages([])
      setAttachmentsPdf([])
      handleClose()
    } catch (error) {
      if (!isMountedRef.current) return
      showToast({ type: 'error', message: formatErrorMessage(error) })
    } finally {
      if (isMountedRef.current) setIsLoading(false)
    }
  }, [
    attachmentsImages,
    attachmentsPdf,
    confirmAction,
    details,
    dispatch,
    handleClose,
    onSuccess,
    serviceOrderId,
    showToast,
    successToastMessage,
  ])

  const handleCancel = useCallback(() => {
    setDetails('')
    setAttachmentsImages([])
    setAttachmentsPdf([])
    handleClose()
  }, [handleClose])

  return (
    <>
      <Loader open={isLoading} hasBackdrop label="Aguarde..." />
      <Modal.Root open={isOpen} disableEscapeKeyDown>
        <Modal.TitleModal title={title} className={styles.titleTight} />
        <Divider className={styles.dividerTight} />
        <Modal.Content className={styles.contentTight}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <Typography style={{ fontSize: 16, textAlign: 'left' }}>{introText}</Typography>
          </div>
          <div
            style={{
              width: '100%',
            }}
          >
            <TextField
              label="Informe os detalhes da garantia"
              placeholder="Mais detalhes"
              multiline
              rows={8}
              value={details}
              maxCharSize={MAX_DETAILS_LENGTH}
              variant="outlined"
              onChange={e => setDetails((e.target.value || '').slice(0, MAX_DETAILS_LENGTH))}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 4,
                color: '#6b7280',
                fontSize: 8,
              }}
            >
              {details?.length || 0}/{MAX_DETAILS_LENGTH}
            </div>
          </div>
          <div style={{ width: '100%' }}>
            <Attachments
              label="Imagens e vídeos"
              readOnly={false}
              files={attachmentsImages}
              accept={{
                'image/jpeg': [
                  '.jpeg',
                  '.png',
                  '.jpg',
                  '.bmp',
                  '.webp',
                  '.mkv',
                  '.mp4',
                  '.mov',
                  '.avi',
                  '.m4v',
                  '.mpg',
                  '.mpeg',
                  '.wmv',
                  '.webm',
                ],
              }}
              onDrop={files => setAttachmentsImages(old => [...old, ...files])}
              onRemoveItem={index => setAttachmentsImages(old => old.filter((_, i) => i !== index))}
              onItemClick={() => {}}
            />
          </div>
          <div style={{ width: '100%' }}>
            <Attachments
              label="Arquivos PDF"
              content="files"
              readOnly={false}
              files={attachmentsPdf}
              accept={{ 'application/pdf': ['.pdf'] }}
              onDrop={files => setAttachmentsPdf(old => [...old, ...files])}
              onRemoveItem={index => setAttachmentsPdf(old => old.filter((_, i) => i !== index))}
              onItemClick={() => {}}
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" variant="secondary" onClick={handleCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button color="primary" variant="primary" onClick={handleConfirm} disabled={isLoading}>
            Salvar
          </Button>
        </Modal.Actions>
      </Modal.Root>
    </>
  )
}

WarrantyOpeningModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  serviceOrderId: PropTypes.string,
  title: PropTypes.string,
  introText: PropTypes.string,
  confirmAction: PropTypes.func,
  onSuccess: PropTypes.func,
  successToastMessage: PropTypes.string,
}

WarrantyOpeningModal.defaultProps = {
  serviceOrderId: null,
  title: 'Abertura de garantia',
  introText: 'Deseja mover o chamado para a etapa de garantia?',
  confirmAction: null,
  onSuccess: null,
  successToastMessage: 'Garantia iniciada com sucesso.',
}
